import React, { useEffect, useRef, useState, } from 'react';
import PropTypes from 'prop-types';
import './LockScreen.css';

const LockScreen = ({ onUnlock, onSignInAgain, userEmail, }) => {
  const [ username, setUsername, ] = useState('');
  const [ password, setPassword, ] = useState('');
  const [ error, setError, ] = useState('');
  const [ loading, setLoading, ] = useState(false);
  const [ signingOut, setSigningOut, ] = useState(false);
  const usernameInput = useRef(null);
  const busy = loading || signingOut;

  useEffect(() => {
    if (usernameInput.current) {
      usernameInput.current.focus();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onUnlock(username, password);
    } catch (err) {
      setError(err.message || 'Invalid credentials. Please try again.');
      setPassword('');
      setLoading(false);
    }
  };

  const handleSignInAgain = async () => {
    setError('');
    setSigningOut(true);

    try {
      await onSignInAgain();
    } catch (err) {
      // The container lowers the overlay either way, so this only matters if that
      // somehow fails — leave the button usable rather than stuck on 'Signing out'.
      setError(err.message || 'Could not sign out. Please try again.');
      setSigningOut(false);
    }
  };

  return (
    <div className="lock-screen-overlay">
      <div className="lock-screen-container">
        <div className="lock-screen-content">
          <div className="lock-icon">🔒</div>
          <h2>Session Locked</h2>
          {userEmail && <div className="user-info">{userEmail}</div>}
          <p className="lock-message">
            Your session is locked for security. Please enter your credentials to continue.
          </p>

          <form onSubmit={handleSubmit} className="lock-screen-form">
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                ref={usernameInput}
                disabled={busy}
                required
              />
            </div>
            <div className="mb-3">
              <input
                type="password"
                className="form-control"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                required
              />
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={busy || !username || !password}
            >
              {loading ? 'Unlocking...' : 'Unlock'}
            </button>
          </form>

          <div className="lock-screen-footer">
            <small>Your work has been preserved and will be available after unlocking.</small>
          </div>

          {/* Unlocking is a password sign-in, and SSO users have no password to
              enter — without this they would be stuck behind the overlay. */}
          <div className="lock-screen-alternate">
            <button
              type="button"
              className="btn btn-link lock-screen-restart"
              onClick={handleSignInAgain}
              disabled={busy}
            >
              {signingOut ? 'Signing out...' : 'Sign in again'}
            </button>
            <small className="lock-screen-restart-hint">
              Use this if you sign in with SSO — there is no password to enter above.
              It ends this session and discards unsaved work in this tab.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

LockScreen.propTypes = {
  onUnlock: PropTypes.func.isRequired,
  onSignInAgain: PropTypes.func.isRequired,
  userEmail: PropTypes.string,
};

export default LockScreen;
